import { type BannerVariant, Toast, Toaster } from '@backend/lib-design-system';
import { createContext, type PropsWithChildren, useContext, useState } from 'react';

import type { OnClose, SetToastType, ToastContextType, ToastType } from '~/contexts/ToastContext/types';

export const ToastContext = createContext<ToastContextType>({
  setToastError: () => undefined,
  setToastSuccess: () => undefined,
  setToastInfo: () => undefined,
  setToastWarning: () => undefined,
  closeToast: () => undefined,
});

export const ToastContextProvider = (props: PropsWithChildren) => {
  const [toast, setToast] = useState<ToastType | null>(null);

  const _setToast = (heading: string, body: string, onClose: OnClose | null, variant: BannerVariant) => {
    setToast({ heading, body, onClose, variant });
  };

  const setToastInfo: SetToastType = (body, { heading = '', onClose = null } = {}) => {
    _setToast(heading, body, onClose, 'info');
  };

  const setToastSuccess: SetToastType = (body, { heading = '', onClose = null } = {}) => {
    _setToast(heading, body, onClose, 'success');
  };

  const setToastWarning: SetToastType = (body, { heading = '', onClose = null } = {}) => {
    _setToast(heading, body, onClose, 'warning');
  };

  const setToastError: SetToastType = (body, { heading = '', onClose = null } = {}) => {
    _setToast(heading, body, onClose, 'error');
  };

  const closeToast = () => setToast(null);

  return (
    <ToastContext.Provider value={{ setToastInfo, setToastSuccess, setToastWarning, setToastError, closeToast }}>
      {props.children}
      {toast ? (
        <Toaster>
          <Toast
            variant={toast.variant}
            heading={toast.heading}
            body={toast.body}
            onClose={() => {
              if (toast.onClose) {
                toast.onClose();
              }
              setToast(null);
            }}
          />
        </Toaster>
      ) : null}
    </ToastContext.Provider>
  );
};

export const useToastContext = () => useContext(ToastContext);
